import { useEffect, useState } from "react";
import { motion } from "motion/react";
import {
  ArrowRight,
  Building2,
  Check,
  ChevronRight,
  CircleCheck,
  Menu,
  MessageCircle,
  ScanLine,
  X
} from "lucide-react";
import { capabilityCards, featureCards, navItems, roleValueRows, structuredFacts } from "./data";

const asset = (name: string) => `/assets/${name}`;
const configuredSiteUrl = (import.meta.env.VITE_SITE_URL || "").replace(/\/+$/, "");

function Logo({ compact = false }: { compact?: boolean }) {
  return (
    <a className={`brand-lockup ${compact ? "brand-lockup--compact" : ""}`} href="#top" aria-label="返回爱药事首页">
      <span className="brand-mark" aria-hidden="true">
        <img src={asset("aiyaoshi-logo.png")} alt="" />
      </span>
      <span className="brand-copy">
        <strong>爱药事</strong>
        <small>行业114 · 医药资源撮合平台</small>
      </span>
    </a>
  );
}

function PhoneFrame({ src, alt, className = "", loading = "lazy" }: { src: string; alt: string; className?: string; loading?: "eager" | "lazy" }) {
  return (
    <div className={`phone-frame ${className}`}>
      <div className="phone-frame__camera" aria-hidden="true" />
      <img src={asset(src)} alt={alt} loading={loading} />
    </div>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [siteUrl, setSiteUrl] = useState(configuredSiteUrl);

  useEffect(() => {
    const url = import.meta.env.VITE_SITE_URL || window.location.origin;
    setSiteUrl(url);
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) canonical.href = url;
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Brand",
        "@id": `${siteUrl || ""}/#brand`,
        name: structuredFacts.name,
        alternateName: structuredFacts.alternateName,
        description: structuredFacts.description,
        logo: `${siteUrl || ""}/assets/aiyaoshi-logo.png`
      },
      {
        "@type": "Organization",
        "@id": `${siteUrl || ""}/#company`,
        name: structuredFacts.operator,
        legalName: structuredFacts.operator,
        description: "爱药事品牌官网的运营主体。",
        url: siteUrl || undefined
      },
      {
        "@type": "WebSite",
        name: "爱药事｜医药行业资源撮合平台",
        description: structuredFacts.description,
        url: siteUrl || undefined,
        inLanguage: "zh-CN",
        about: { "@id": `${siteUrl || ""}/#brand` },
        publisher: { "@id": `${siteUrl || ""}/#company` }
      },
      {
        "@type": "WebPage",
        name: "爱药事官网落地页",
        description: structuredFacts.description,
        url: siteUrl || undefined,
        inLanguage: "zh-CN",
        hasPart: [
          {
            "@type": "WebPageElement",
            name: "平台价值拆解",
            url: `${siteUrl || ""}/#role-value`,
            description: "按医药厂商、代理商与医药代表、医疗机构与终端、行业用户、平台运营说明平台提供的能力与协作价值。"
          },
          {
            "@type": "VideoObject",
            name: "爱药事行业114宣传视频",
            url: `${siteUrl || ""}/#story`,
            description: "从产品、企业、招商到资讯，说明爱药事如何把分散的信息和真实联系放到一条更清晰的业务链路里。",
            contentUrl: `${siteUrl || ""}/assets/aiyaoshi-industry-114-launch-v6.mp4`,
            thumbnailUrl: `${siteUrl || ""}/assets/aiyaoshi-industry-114-launch-poster.jpg`,
            duration: "PT1M27S",
            caption: "视频本身已内嵌中文字幕，页面不额外叠加字幕轨道。",
            inLanguage: "zh-CN"
          }
        ]
      },
      {
        "@type": "SoftwareApplication",
        name: "爱药事小程序",
        applicationCategory: "BusinessApplication",
        operatingSystem: "WeChat Mini Program",
        description: "查产品、看厂商、找招商、读资讯的医药行业协作入口。"
      }
    ]
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <div className="site-shell" id="top">
        <header className="site-header">
          <div className="container nav-wrap">
            <Logo compact />
            <nav id="main-nav" className={`main-nav ${menuOpen ? "main-nav--open" : ""}`} aria-label="主导航">
              {navItems.map((item) => (
                <a key={item.href} href={item.href} onClick={() => setMenuOpen(false)}>
                  {item.label}
                </a>
              ))}
            </nav>
            <div className="nav-actions">
              <a className="nav-link" href="#download">微信扫码进入</a>
              <a className="button button--small" href="#download">扫码打开 <ArrowRight size={16} /></a>
              <button className="menu-toggle" aria-label={menuOpen ? "关闭菜单" : "打开菜单"} aria-controls="main-nav" aria-expanded={menuOpen} onClick={() => setMenuOpen((open) => !open)}>
                {menuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </header>

        <main>
          <section className="hero section" aria-labelledby="hero-title">
            <div className="hero__glow hero__glow--one" aria-hidden="true" />
            <div className="hero__glow hero__glow--two" aria-hidden="true" />
            <div className="container hero-grid">
              <div className="hero-copy">
                <motion.div className="eyebrow" initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
                  <span className="eyebrow-dot" /> 医药行业资源撮合平台
                </motion.div>
                <motion.h1 id="hero-title" initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.08 }}>
                  找人、找药、办事，<span>就上爱药事</span>
                </motion.h1>
                <motion.p className="hero-lede" initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.16 }}>
                  连接每一次更清晰的行业协作。把产品、厂商、招商、资讯与真实联系，放到一条可追踪的业务链路里。
                </motion.p>
                <motion.div className="hero-actions" initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.24 }}>
                  <a className="button" href="#download">查看小程序码 <ArrowRight size={18} /></a>
                  <a className="text-link" href="#positioning">先了解平台 <ChevronRight size={17} /></a>
                </motion.div>
                <div className="hero-proof">
                  <div className="proof-item"><CircleCheck size={17} /> 资源信息更集中</div>
                  <div className="proof-item"><CircleCheck size={17} /> 联系过程可追溯</div>
                  <div className="proof-item"><CircleCheck size={17} /> 按版本逐步开放 AI</div>
                </div>
              </div>
              <motion.div className="hero-visual" initial={false} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }}>
                <img className="hero-network" src={asset("hero-network-v1.png")} alt="浅色医药研发与行业协作网络背景" />
                <div className="hero-visual__label"><span>爱药事 · 行业114</span><small>一站式医药行业协作入口</small></div>
                <PhoneFrame src="home-phone.png" alt="爱药事小程序首页，展示产品资源与行业入口" className="phone-frame--hero" loading="eager" />
                <div className="floating-note floating-note--top"><span className="floating-note__icon"><Check size={15} /></span><span>找到需要的资源</span></div>
                <div className="floating-note floating-note--bottom"><MessageCircle size={17} /> <span>让联系真正发生</span></div>
              </motion.div>
            </div>
          </section>

          <section className="brand-story section" id="story" aria-labelledby="story-title">
            <div className="container brand-story__grid">
              <div className="brand-story__media">
                <div className="brand-story__video-frame">
                  <video controls preload="metadata" playsInline poster={asset("aiyaoshi-industry-114-launch-poster.jpg")} aria-label="爱药事行业114宣传视频">
                    <source src={asset("aiyaoshi-industry-114-launch-v6.mp4")} type="video/mp4" />
                  </video>
                </div>
              </div>
              <div className="brand-story__copy">
                <span className="section-kicker">品牌视频</span>
                <h2 id="story-title">一段视频，<span>看懂爱药事如何连接行业</span></h2>
                <p>从产品、企业、招商到资讯，把分散的信息和真实联系放到一条更清晰的业务链路里。</p>
                <div className="brand-story__steps" aria-label="平台连接路径">
                  <div><span>01</span><strong>先找到</strong><small>产品、企业与招商机会</small></div>
                  <div><span>02</span><strong>再看懂</strong><small>认证、目录与行业信息</small></div>
                  <div><span>03</span><strong>发生联系</strong><small>让每一次查找走向下一步</small></div>
                </div>
                <a className="text-link" href="#download">看完就去小程序 <ArrowRight size={17} /></a>
              </div>
            </div>
          </section>

          <section className="positioning section section--tight" id="positioning" aria-labelledby="positioning-title">
            <div className="container">
              <div className="section-heading section-heading--center">
                <span className="section-kicker">为什么是爱药事</span>
                <h2 id="positioning-title">不只是信息展示，<span>更是资源协作</span></h2>
                <p>让行业里的每一次查找，都能自然走向理解、信任与下一步行动。</p>
              </div>
              <div className="role-rail" aria-label="平台连接的行业角色">
                {structuredFacts.audiences.map((audience, index) => (
                  <div className="role-pill" key={audience}>
                    <span className="role-pill__index">0{index + 1}</span>
                    <span>{audience}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="role-value section" id="role-value" aria-labelledby="role-value-title">
            <div className="container">
              <div className="section-heading section-heading--center role-value__heading">
                <span className="section-kicker">平台价值拆解</span>
                <h2 id="role-value-title">平台为每一个角色，<span>提供更清晰的下一步</span></h2>
                <p>连接人、产品、企业与招商机会，让医药行业的每一次选择更高效。</p>
              </div>
              <div className="role-value-grid" aria-label="爱药事服务角色与平台价值">
                {roleValueRows.map((row, index) => {
                  const Icon = row.icon;
                  return (
                    <motion.article
                      className={`role-value-card role-value-card--${row.tone}`}
                      key={row.role}
                      initial={false}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.2 }}
                      transition={{ duration: 0.42, delay: index * 0.04 }}
                    >
                      <div className="role-value-card__top">
                        <span className="role-value-card__icon"><Icon size={20} strokeWidth={1.8} /></span>
                        <span className="role-value-card__index">0{index + 1}</span>
                      </div>
                      <h3>{row.role}</h3>
                      <div className="role-value-card__outcome">
                        <span>真正获得</span>
                        <strong>{row.outcome}</strong>
                      </div>
                      <div className="role-value-card__provide">
                        <span>平台提供</span>
                        <p>{row.provide}</p>
                      </div>
                    </motion.article>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="resources section" id="resources" aria-labelledby="resources-title">
            <div className="container">
              <div className="section-heading">
                <span className="section-kicker">从一个清晰入口开始</span>
                <h2 id="resources-title">你要找的，<span>就在这一页</span></h2>
                <p>四个高频入口，覆盖医药行业最重要的日常协作场景。</p>
              </div>
              <div className="feature-grid">
                {featureCards.map((card, index) => {
                  const Icon = card.icon;
                  return (
                    <motion.article className={`feature-card feature-card--${card.tone}`} key={card.title} whileHover={{ y: -5 }} transition={{ duration: 0.2 }}>
                      <div className="feature-card__top"><span>{card.eyebrow}</span><span className="feature-card__number">0{index + 1}</span></div>
                      <div className="icon-tile"><Icon size={24} strokeWidth={1.8} /></div>
                      <h3>{card.title}</h3>
                      <p>{card.description}</p>
                      <a className="card-link" href="#download">查看扫码入口 <ArrowRight size={16} /></a>
                    </motion.article>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="enterprise section" id="enterprise" aria-labelledby="enterprise-title">
            <div className="container split-section">
              <div className="split-copy">
                <span className="section-kicker">企业主页</span>
                <h2 id="enterprise-title">从一张企业主页，<span>看见完整业务</span></h2>
                <p>企业不再只是一个名称。产品目录、招商机会、企业动态、联系人与认证信息，被组织成一个可以理解、可以行动的主页。</p>
                <ul className="check-list">
                  <li><Check size={17} /> 认证信息与企业主体清晰可见</li>
                  <li><Check size={17} /> 产品、招商和动态自然关联</li>
                  <li><Check size={17} /> 从浏览到联系，形成可追踪线索</li>
                </ul>
                <a className="text-link" href="#download">扫码查看企业主页 <ChevronRight size={17} /></a>
              </div>
              <div className="split-visual split-visual--mint">
                <div className="visual-tag visual-tag--enterprise">企业主页 · 业务全景</div>
                <PhoneFrame src="enterprise-home-phone.png" alt="爱药事小程序企业主页，展示企业信息和产品目录" />
                <div className="orbit-card orbit-card--enterprise"><Building2 size={18} /><span>产品、招商、动态、联系</span></div>
              </div>
            </div>
          </section>

          <section className="lead-loop section" aria-labelledby="loop-title">
            <div className="container split-section split-section--reverse">
              <div className="split-visual split-visual--blue">
                <div className="visual-tag visual-tag--blue">从浏览到有效线索</div>
                <PhoneFrame src="manufacturers-phone.png" alt="爱药事小程序厂商列表，展示企业与产品联系入口" />
                <div className="loop-chip loop-chip--one">浏览</div>
                <div className="loop-chip loop-chip--two">收藏 / 咨询</div>
                <div className="loop-chip loop-chip--three">有效线索</div>
              </div>
              <div className="split-copy">
                <span className="section-kicker">让每一次查找都有下一步</span>
                <h2 id="loop-title">不是流量停留，<span>而是关系发生</span></h2>
                <p>平台把查看企业、收藏产品、咨询联系人等动作，沉淀为更清晰的协作反馈。推荐不靠猜，而是从真实业务行为逐步变准。</p>
                <div className="steps-line">
                  {["查看", "收藏 / 咨询", "有效线索"].map((step, index) => (
                    <div className="step" key={step}><span>{index + 1}</span><strong>{step}</strong>{index < 2 && <ArrowRight size={16} />}</div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="content-insight section" id="content" aria-labelledby="content-title">
            <div className="container content-grid">
              <div className="content-copy">
                <span className="section-kicker">行业资讯与查询工具</span>
                <h2 id="content-title">重要信息，<span>值得被看见</span></h2>
                <p>政策、市场动态、医院通知和资料附件统一呈现。搜得到、看得懂、留得住，才真正能服务下一次判断。</p>
                <div className="content-stat-row"><span><strong>政策</strong><small>及时同步</small></span><span><strong>市场</strong><small>持续更新</small></span><span><strong>资料</strong><small>便于查阅</small></span></div>
              </div>
              <div className="content-visual">
                <div className="content-visual__accent" aria-hidden="true" />
                <PhoneFrame src="news-phone.png" alt="爱药事小程序资讯列表，展示行业资讯和市场动态" />
                <PhoneFrame src="query-phone.png" alt="爱药事小程序查询工具，展示药品查询结果" className="phone-frame--overlap" />
              </div>
            </div>
          </section>

          <section className="capabilities section" id="capabilities" aria-labelledby="capabilities-title">
            <div className="container">
              <div className="section-heading section-heading--center">
                <span className="section-kicker">平台能力</span>
                <h2 id="capabilities-title">把复杂的行业协作，<span>做得更清楚</span></h2>
                <p>爱药事的能力不是一次性堆满，而是围绕真实使用持续迭代。</p>
              </div>
              <div className="capability-grid">
                {capabilityCards.map((card) => {
                  const Icon = card.icon;
                  return <article className="capability-card" key={card.title}><div className="capability-icon"><Icon size={22} /></div><h3>{card.title}</h3><p>{card.description}</p></article>;
                })}
              </div>
              <div className="ai-callout">
                <div className="ai-callout__art"><img src={asset("ai-phone.png")} alt="爱药事小程序智能助手界面" loading="lazy" /></div>
                <div className="ai-callout__copy"><span className="section-kicker">AI 与智能推荐</span><h3>让平台越来越懂你，<span>但始终以真实业务为先</span></h3><p>平台内容优先回答；在适合的版本与权限范围内，再逐步开放智能问答、检索、匹配与运营辅助能力。</p><span className="ai-note"><span className="ai-note__dot" /> 按版本开放 · 受控 · 可追溯</span></div>
              </div>
            </div>
          </section>

          <section className="download section" id="download" aria-labelledby="download-title">
            <div className="container download-card">
              <div className="download-copy"><span className="section-kicker">现在就开始</span><h2 id="download-title">找人找药办事，<span>就上爱药事</span></h2><p>打开微信，扫码进入小程序。也可以关注公众号，持续获取行业信息。</p><div className="download-actions"><a className="button" href="#mini-program-qr"><ScanLine size={18} /> 扫码进入</a><a className="text-link text-link--light" href="#enterprise">我是企业 / 厂商 <ArrowRight size={16} /></a></div></div>
              <div className="qr-grid"><div className="qr-item" id="mini-program-qr"><img src={asset("mini-program-qr-latest.jpg")} alt="爱药事小程序码" /><strong>爱药事小程序</strong><span>微信扫一扫进入</span></div><div className="qr-item"><img src={asset("official-account-qr-latest.jpg")} alt="爱药事公众号二维码" /><strong>爱药事公众号</strong><span>关注获取行业资讯</span></div></div>
            </div>
          </section>
        </main>

        <footer className="site-footer">
          <div className="container footer-grid">
            <div><Logo /><p>连接每一次更清晰的行业协作</p><small className="footer-operator">爱药事品牌官网｜运营主体：四川爱药事科技有限公司</small></div>
            <div className="footer-links"><div><strong>了解平台</strong><a href="#positioning">平台定位</a><a href="#role-value">角色价值</a><a href="#resources">产品资源</a><a href="#enterprise">企业主页</a></div><div><strong>继续探索</strong><a href="#content">资讯与洞察</a><a href="#story">宣传视频</a><a href="#capabilities">平台能力</a><a href="#download">扫码进入</a></div></div>
            <div className="footer-badge"><span>爱药事 · 行业114</span><small>医药资源撮合平台</small></div>
          </div>
          <div className="container footer-bottom"><span>© 2026 爱药事 · 行业114</span><a className="icp-link" href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer">蜀ICP备2025176659号-1</a><span>为行业连接真实的人与资源</span></div>
        </footer>
      </div>
    </>
  );
}

export default App;
